var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useContext, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { playAudio } from '../helper/audioHelper';
import { saveState } from '../helper/stateManagerHelper';
import { announceAssertive } from '../helper/announcer';
import { getFractionDimensions, sumAndReduceFractions, parseFraction, gcd } from '../helper/helper';
import { getDenominatorFromFractionType } from './SplitFractionTileModal';
import { getSnappedPosition, findTouchingParent, solveDockMatchingScale, getGlobalFractionTileScale, } from '../helper/snapHelper';
import { snapTileToNumberLine } from '../helper/numberLineHelper';
import { FRACTION_CONFIG } from '../constants/fractionConfig';
import globalStore from '../store';
var FractionTileController = function () {
    var _a = useContext(DataContext), manipulative = _a.manipulative, setManipulative = _a.setManipulative, selectedTool = _a.selectedTool, setSelectedTool = _a.setSelectedTool, selectedManipulativeId = _a.selectedManipulativeId, setSelectedManipulativeId = _a.setSelectedManipulativeId, setShowSplitFractionTileModal = _a.setShowSplitFractionTileModal, setSplitFractionTileCurrentDenominator = _a.setSplitFractionTileCurrentDenominator, setSplitFractionTileCurrentNumerator = _a.setSplitFractionTileCurrentNumerator, savedState = _a.savedState;
    var _b = globalStore.useStoreState(function (state) { return state.playerStore; }), zoomLevel = _b.zoomLevel, playerBgPattern = _b.playerBgPattern;
    useEffect(function () {
        var handleEvents = function (data) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8;
            var newStateToSave = [];
            switch (data.id) {
                case 'createFractionTile':
                    if (data.data) {
                        var _9 = data.data, fractionType = _9.fractionType, dropX = _9.dropX, dropY = _9.dropY, eventShapeType = _9.shapeType, eventLablesType = _9.lablesType, eventColor = _9.color;
                        // Convert screen coordinates to canvas coordinates.
                        // Use .mainHolder (the CSS-scaled canvas element) as the origin so the
                        // margin: auto centering of the canvas at zoom < 100% is accounted for.
                        // Fallback to the scroll container only if mainHolder isn't found.
                        var scrollEl = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
                        var mainBoard = document.querySelector('.mainHolder');
                        var canvasEl = mainBoard || scrollEl;
                        if (canvasEl) {
                            var rect = canvasEl.getBoundingClientRect();
                            // Get the currently selected fraction tile to match its properties
                            var selectedFractionTile = (_a = manipulative.find(function (m) { return m.data.type === 'fractionTiles' && m.data.id === selectedManipulativeId; })) === null || _a === void 0 ? void 0 : _a.data;
                            var shapeType_1 = eventShapeType || (selectedFractionTile === null || selectedFractionTile === void 0 ? void 0 : selectedFractionTile.shapeType) || 'rectangle';
                            var lablesType = eventLablesType || (selectedFractionTile === null || selectedFractionTile === void 0 ? void 0 : selectedFractionTile.lablesType) || 'labeled';
                            var color = (_b = eventColor !== null && eventColor !== void 0 ? eventColor : selectedFractionTile === null || selectedFractionTile === void 0 ? void 0 : selectedFractionTile.color) !== null && _b !== void 0 ? _b : '';
                            var config = getFractionDimensions(fractionType);
                            var rectDims = config ? config.rectangle : { width: 120, height: 55 };
                            // Match the shared/global scale already in use for this shape type
                            // on the whiteboard — prefer a non-NL-attached sibling's own
                            // `scale`, since an NL-attached tile's `scale` is forced by the
                            // number line and isn't representative of the group's actual
                            // shared scale. If every existing tile of this shape happens to
                            // be NL-attached, fall back to one of their `preSnapScale`
                            // (the shared scale they'd restore to on unsnap, kept fresh on
                            // every snap) rather than their NL-forced `scale`.
                            // Shared with the panel-drag ghost (FractionButton) so both use
                            // the same definition of "the global fraction-tile scale".
                            var tileScale = getGlobalFractionTileScale(manipulative, shapeType_1);
                            var baseWidth = shapeType_1 === 'circle' ? 300 : rectDims.width;
                            var baseHeight = shapeType_1 === 'circle' ? 300 : rectDims.height;
                            var dimensions_1 = {
                                width: baseWidth * tileScale,
                                height: baseHeight * tileScale,
                            };
                            var x = (dropX - rect.left) / zoomLevel;
                            var y = (dropY - rect.top) / zoomLevel;
                            var canvasX = Math.round(x - dimensions_1.width / 2);
                            var canvasY = Math.round(y - dimensions_1.height / 2);
                            // Create new fraction tile
                            var newTileId = "fractionTiles_".concat(uuidv4().replace(/-/g, ''));
                            // --- SIZE TO DOCK/ATTACH AT (before tile-to-tile docking) ---
                            // Determine the size for the docking computation below. Prefer
                            // matching an adjacent existing tile's own rendered scale
                            // directly — that neighbor's size is ground truth for what a
                            // docked tile must match, whatever put it at that size. Only
                            // fall back to a number-line proximity guess when there's no
                            // neighbor tile to match yet (e.g. dropping the first tile onto
                            // an empty stretch of the line). Docking first with the wrong
                            // size and correcting scale afterward would leave a gap/overlap,
                            // since the docked edge was computed against the wrong size.
                            // Baseline size for the dock solve — only has to be right for
                            // the no-dock case; `solveDockMatchingScale` corrects it to the
                            // dock target's own scale and re-solves. The old
                            // old `findAdjacentFractionTileScale` pre-check (now removed) needed edge contact
                            // within 5px of the drop point while the solver docks within
                            // 55px, so a tile created next to a number-line-attached tile
                            // kept its own (panel) scale across most of the catch radius.
                            var prospectiveNlSnap = shapeType_1 === 'rectangle'
                                ? snapTileToNumberLine({ x: canvasX, y: canvasY }, { width: baseWidth, height: baseHeight }, tileScale, manipulative, newTileId)
                                : null;
                            var baselineDockDimensions = prospectiveNlSnap
                                ? {
                                    width: baseWidth * prospectiveNlSnap.scale,
                                    height: baseHeight * prospectiveNlSnap.scale,
                                }
                                : dimensions_1;
                            // --- END SIZE TO DOCK/ATTACH AT ---
                            var dockDimensions = baselineDockDimensions;
                            var adjacent = void 0;
                            var snapResult_1;
                            if (shapeType_1 === 'rectangle') {
                                var solved = solveDockMatchingScale({ x: canvasX, y: canvasY }, baselineDockDimensions, { width: baseWidth, height: baseHeight }, manipulative, newTileId, undefined, { fractionSize: fractionType });
                                snapResult_1 = solved.snap;
                                dockDimensions = solved.dimensions;
                                adjacent = solved.dockedTo;
                            }
                            else {
                                snapResult_1 = getSnappedPosition({ x: canvasX, y: canvasY }, dockDimensions, shapeType_1, manipulative, newTileId, undefined, { fractionSize: fractionType });
                            }
                            // --- GRID SNAPPING LOGIC FOR PLACEMENT ---
                            var finalX = snapResult_1.x;
                            var finalY = snapResult_1.y;
                            var finalScale = tileScale;
                            var finalDimensions = dimensions_1;
                            var nlSnapNumberLineId = void 0;
                            // --- NUMBER LINE SNAPPING LOGIC (dropped from the tool panel) ---
                            // Mirrors the same check in FractionTileManipulative.tsx's drag-drop
                            // handler — without this, a tile created here (rather than dragged
                            // on-canvas) never snapped to a nearby Number Line.
                            if (shapeType_1 === 'rectangle') {
                                var isTileDocked = snapResult_1.isSnappedX || snapResult_1.isSnappedY;
                                if (isTileDocked && adjacent) {
                                    // The neighbor's own size is already ground truth — dockDimensions
                                    // above used it, so snapResult.x/y is already geometrically
                                    // correct and needs no further correction. Just adopt the same
                                    // scale/dimensions/numberLineId (if any) as the neighbor.
                                    finalScale = adjacent.scale;
                                    finalDimensions = dockDimensions;
                                    nlSnapNumberLineId = adjacent.numberLineId;
                                }
                                else {
                                    // No neighbor to match — fall back to the number-line proximity
                                    // check, which drives both position and scale directly (its y
                                    // is derived from real tick geometry).
                                    var nlCheckPos = isTileDocked ? { x: finalX, y: finalY } : { x: canvasX, y: canvasY };
                                    var nlSnap = snapTileToNumberLine(nlCheckPos, { width: baseWidth, height: baseHeight }, tileScale, manipulative, newTileId);
                                    if (nlSnap) {
                                        if (!isTileDocked) {
                                            finalX = nlSnap.x;
                                        }
                                        finalY = nlSnap.y;
                                        finalScale = nlSnap.scale;
                                        finalDimensions = {
                                            width: baseWidth * nlSnap.scale,
                                            height: baseHeight * nlSnap.scale,
                                        };
                                        nlSnapNumberLineId = nlSnap.numberLineId;
                                    }
                                }
                            }
                            // --- END NUMBER LINE SNAPPING LOGIC ---
                            var GRID_SIZE = 55;
                            if (playerBgPattern === 'square' &&
                                shapeType_1 === 'rectangle' &&
                                !snapResult_1.isSnappedX &&
                                !snapResult_1.isSnappedY &&
                                !nlSnapNumberLineId) {
                                finalX = Math.round(canvasX / GRID_SIZE) * GRID_SIZE;
                                finalY = Math.round(canvasY / GRID_SIZE) * GRID_SIZE;
                            }
                            // Play snap sound when tile snaps to another tile on drop from panel
                            if (snapResult_1.isSnappedX || snapResult_1.isSnappedY) {
                                playAudio('PatternAdd');
                            }
                            // ----------------------------------------
                            // --- SNAPPED ID ASSIGNMENT FOR RIGHT-PANEL DROP ---
                            // Assign snappedId so scale repositioning can chain this tile with its neighbours.
                            // Only applies to rectangles (circles use center-sharing, not snappedId).
                            var newTileSnappedId_1;
                            if (shapeType_1 === 'rectangle') {
                                var modIds = new Set(((_c = snapResult_1.modifications) !== null && _c !== void 0 ? _c : []).map(function (m) { return m.id; }));
                                var finalPos_1 = { x: finalX, y: finalY };
                                // 1. Inherit from mod tiles (row-insertion neighbours).
                                for (var _i = 0, manipulative_1 = manipulative; _i < manipulative_1.length; _i++) {
                                    var m = manipulative_1[_i];
                                    if (m.data.type !== 'fractionTiles')
                                        continue;
                                    var d = m.data;
                                    if (!modIds.has(d.id))
                                        continue;
                                    if (d.snappedId) {
                                        newTileSnappedId_1 = d.snappedId;
                                        break;
                                    }
                                }
                                // 2. Inherit from adjacent tiles (horizontal or vertical neighbours).
                                if (!newTileSnappedId_1) {
                                    var isAdjacentToNew = function (d) {
                                        if (d.shapeType !== 'rectangle')
                                            return false;
                                        var sameRow = Math.abs(d.center.y - finalPos_1.y) < 10;
                                        var sameCol = d.center.x < finalPos_1.x + dimensions_1.width + 5 &&
                                            d.center.x + d.dimensions.width > finalPos_1.x - 5;
                                        if (sameRow) {
                                            var myRight = d.center.x + d.dimensions.width;
                                            return (Math.abs(finalPos_1.x - myRight) < 5 ||
                                                Math.abs(finalPos_1.x + dimensions_1.width - d.center.x) < 5);
                                        }
                                        if (sameCol) {
                                            var dBottom = d.center.y + d.dimensions.height;
                                            // Use tight tolerance (5px) so only nearly-touching vertical placements
                                            // inherit the cluster — prevents separate groups from merging.
                                            return (Math.abs(finalPos_1.y - dBottom) < 5 ||
                                                Math.abs(finalPos_1.y + dimensions_1.height - d.center.y) < 5);
                                        }
                                        return false;
                                    };
                                    for (var _10 = 0, manipulative_2 = manipulative; _10 < manipulative_2.length; _10++) {
                                        var m = manipulative_2[_10];
                                        if (m.data.type !== 'fractionTiles')
                                            continue;
                                        var d = m.data;
                                        if (modIds.has(d.id) || d.id === newTileId)
                                            continue;
                                        if (isAdjacentToNew(d) && d.snappedId) {
                                            newTileSnappedId_1 = d.snappedId;
                                            break;
                                        }
                                    }
                                }
                                // 3. No existing cluster — create a new one.
                                if (!newTileSnappedId_1)
                                    newTileSnappedId_1 = uuidv4();
                            }
                            // -----------------------------------------------
                            // --- SNAPPED TO ASSIGNMENT FOR RIGHT-PANEL DROP ---
                            // Sole source of truth for scale repositioning; must be kept in sync
                            // with the actual drop position, never left stale.
                            var newTileSnappedTo = void 0;
                            if (shapeType_1 === 'rectangle') {
                                var candidates = manipulative
                                    .filter(function (m) { return m.data.type === 'fractionTiles'; })
                                    .map(function (m) {
                                    var _a;
                                    var d = m.data;
                                    var mod = ((_a = snapResult_1.modifications) !== null && _a !== void 0 ? _a : []).find(function (mm) { return mm.id === d.id; });
                                    return mod ? __assign(__assign({}, d), { center: mod.center }) : d;
                                });
                                newTileSnappedTo =
                                    (_d = findTouchingParent({ x: finalX, y: finalY }, finalDimensions, candidates)) !== null && _d !== void 0 ? _d : undefined;
                            }
                            // -----------------------------------------------
                            var newTile_1 = {
                                type: 'fractionTiles',
                                data: {
                                    id: newTileId,
                                    type: 'fractionTiles',
                                    center: { x: finalX, y: finalY },
                                    dimensions: finalDimensions,
                                    defaultDimensions: { width: baseWidth, height: baseHeight },
                                    scale: finalScale,
                                    lock: {
                                        positioning: false,
                                        editing: false,
                                    },
                                    fractionSize: fractionType,
                                    shapeType: shapeType_1,
                                    lablesType: lablesType,
                                    customLabel: '',
                                    color: color,
                                    rotation: (_e = snapResult_1.rotation) !== null && _e !== void 0 ? _e : 0,
                                    snappedId: newTileSnappedId_1,
                                    snappedTo: newTileSnappedTo,
                                    numberLineId: nlSnapNumberLineId,
                                    // Shared/global scale to restore to on unsnap — prefer an
                                    // existing non-NL sibling's current scale over this new
                                    // tile's own requested scale, so it matches the rest of the
                                    // group rather than whatever scale it happened to spawn at.
                                    preSnapScale: nlSnapNumberLineId
                                        ? ((_h = (_g = (_f = manipulative.find(function (m) {
                                            return m.data.type === 'fractionTiles' &&
                                                m.data.shapeType === shapeType_1 &&
                                                !m.data.numberLineId;
                                        })) === null || _f === void 0 ? void 0 : _f.data) === null || _g === void 0 ? void 0 : _g.scale) !== null && _h !== void 0 ? _h : tileScale)
                                        : undefined,
                                },
                                widgetId: "fractionTiles:".concat(uuidv4().replace(/-/g, '')),
                            };
                            var stateChanges_1 = [];
                            stateChanges_1.push({
                                type: newTileId,
                                id: newTileId.split('_')[1],
                                propertyName: 'create',
                                old: JSON.stringify(newTile_1),
                            });
                            var modifiedTiles_1 = snapResult_1.modifications || [];
                            // Compute undo entries using the snapshot at event time (acceptable for history).
                            modifiedTiles_1.forEach(function (mod) {
                                var _a;
                                var m = manipulative.find(function (item) { return item.data.id === mod.id; });
                                if (!m)
                                    return;
                                var d = m.data;
                                stateChanges_1.push({
                                    type: m.data.id || '',
                                    id: (m.data.id || '').split('_')[1],
                                    propertyName: 'center',
                                    old: JSON.stringify(m.data.center),
                                    new: JSON.stringify(mod.center),
                                });
                                if (newTileSnappedId_1 && d.snappedId !== newTileSnappedId_1) {
                                    stateChanges_1.push({
                                        type: m.data.id || '',
                                        id: (m.data.id || '').split('_')[1],
                                        propertyName: 'snappedId',
                                        old: JSON.stringify((_a = d.snappedId) !== null && _a !== void 0 ? _a : null),
                                        new: JSON.stringify(newTileSnappedId_1),
                                    });
                                }
                            });
                            // Apply all state changes
                            newStateToSave.push.apply(newStateToSave, stateChanges_1);
                            // Functional update so tiles added by concurrent handlers are preserved in prev.
                            setManipulative(function (prev) {
                                var updated = prev.map(function (m) {
                                    var mod = modifiedTiles_1.find(function (modItem) { return modItem.id === m.data.id; });
                                    if (mod) {
                                        var d = m.data;
                                        return __assign(__assign({}, m), { data: __assign(__assign(__assign({}, m.data), { center: mod.center }), (newTileSnappedId_1 && d.snappedId !== newTileSnappedId_1
                                                ? { snappedId: newTileSnappedId_1 }
                                                : {})) });
                                    }
                                    return m;
                                });
                                return __spreadArray(__spreadArray([], updated, true), [newTile_1], false);
                            });
                            setSelectedTool([newTileId]);
                            setSelectedManipulativeId(newTileId);
                        }
                    }
                    break;
                case 'glueFractionTiles': {
                    var explicitIds = (_j = data.data) === null || _j === void 0 ? void 0 : _j.tileIds;
                    var stripLabel_1 = function (t) { return t.replace(/_custom_label_(rect|circle)_\d+$/, ''); };
                    // Resolve any manipulativeGroup_ token to its member fraction-tile ids —
                    // otherwise clicking Combine while a group is selected silently no-ops,
                    // since no manipulative item's id equals the groupId itself.
                    var idsToUse = (explicitIds !== null && explicitIds !== void 0 ? explicitIds : selectedTool).flatMap(function (sid) {
                        if (sid.startsWith('manipulativeGroup_')) {
                            return manipulative
                                .filter(function (m) { return m.data.type === 'fractionTiles' && m.data.groupId === sid; })
                                .map(function (m) { return m.data.id; });
                        }
                        return [stripLabel_1(sid)];
                    });
                    var selectedTiles = idsToUse
                        .map(function (sid) { return manipulative.find(function (m) { return m.data.id === sid; }); })
                        .filter(function (m) { return m && m.data.type === 'fractionTiles'; });
                    var circleTiles = selectedTiles.filter(function (t) { return t.data.shapeType === 'circle'; });
                    var rectTiles = selectedTiles.filter(function (t) { return t.data.shapeType === 'rectangle'; });
                    var newGluedTiles_1 = [];
                    var removedTileIds_1 = [];
                    var stateChanges = [];
                    // Expand a selected tile into its flat list of true leaf pieces. If the
                    // tile is already a compound (non-empty gluedFrom), recurse into its
                    // leaves instead of nesting the whole compound as one gluedFrom entry —
                    // gluedFrom must always stay flat (every consumer in the codebase reads
                    // it as a flat array of leaves). Without this, selecting 2+ already-glued
                    // tiles and combining them again produced a gluedFrom entry that itself
                    // had a non-empty gluedFrom, which the unglue logic (one level deep only)
                    // couldn't fully unwind — leaving stale/mismatched undo-redo history.
                    var flattenToLeaves_1 = function (t) {
                        var td = t.data;
                        if (td.gluedFrom && td.gluedFrom.length > 0) {
                            return td.gluedFrom.flatMap(function (leaf) {
                                return leaf.gluedFrom && leaf.gluedFrom.length > 0
                                    ? flattenToLeaves_1({ type: 'fractionTiles', data: leaf })
                                    : [JSON.parse(JSON.stringify(leaf))];
                            });
                        }
                        return [JSON.parse(JSON.stringify(td))];
                    };
                    // Helper to get absolute start angle for circles.
                    // Rounded to the nearest integer degree — standard fraction wedges always
                    // land on exact integer degrees (e.g. 360/12=30), and snapHelper's adjacency
                    // checks use a 1deg-resolution grid. Without rounding, floating-point error
                    // from repeated glue operations slowly drifts the stored rotation off that
                    // grid until adjacency checks start silently failing after several
                    // glue/split cycles (most visible with small wedges like twelfths).
                    var getAbsoluteStart_1 = function (t) {
                        var _a, _b, _c;
                        var td = t.data;
                        if (td.customSweep != null)
                            return Math.round(((((_a = td.rotation) !== null && _a !== void 0 ? _a : 0) % 360) + 360) % 360);
                        var config = ((_b = getFractionDimensions(td.fractionSize)) === null || _b === void 0 ? void 0 : _b.circle) || {
                            start: 0,
                            sweep: 360,
                        };
                        return Math.round((((config.start + ((_c = td.rotation) !== null && _c !== void 0 ? _c : 0)) % 360) + 360) % 360);
                    };
                    // 1. Glue all selected circle tiles into one (if 2+)
                    if (circleTiles.length >= 2) {
                        // Sort by absolute visual start angle for natural clockwise arrangement
                        circleTiles.sort(function (a, b) { return getAbsoluteStart_1(a) - getAbsoluteStart_1(b); });
                        // A selection that skips some wedges can wrap across the 0deg/360deg
                        // boundary (e.g. the un-selected wedges sit right before 0 deg). Sorting
                        // by raw angle then always starts the arc at the smallest angle, which
                        // is wrong when that angle isn't actually where the selected run
                        // begins — it silently reconstructs the wrong arc, moving the combined
                        // sector to a different part of the circle. Find the largest circular
                        // gap between consecutive selected wedges (the removed chunk) and
                        // start the run right after it instead.
                        var getSweep = function (t) {
                            var _a, _b, _c, _d;
                            var td = t.data;
                            return (_d = (_a = td.customSweep) !== null && _a !== void 0 ? _a : (_c = (_b = getFractionDimensions(td.fractionSize)) === null || _b === void 0 ? void 0 : _b.circle) === null || _c === void 0 ? void 0 : _c.sweep) !== null && _d !== void 0 ? _d : 0;
                        };
                        var gapStartIndex = 0;
                        var largestGap = -Infinity;
                        for (var i = 0; i < circleTiles.length; i++) {
                            var cur = circleTiles[i];
                            var next = circleTiles[(i + 1) % circleTiles.length];
                            var curEnd = getAbsoluteStart_1(cur) + getSweep(cur);
                            var nextStart = getAbsoluteStart_1(next) + (i === circleTiles.length - 1 ? 360 : 0);
                            var gap = nextStart - curEnd;
                            if (gap > largestGap) {
                                largestGap = gap;
                                gapStartIndex = (i + 1) % circleTiles.length;
                            }
                        }
                        if (largestGap > 0.01 && gapStartIndex > 0) {
                            circleTiles = __spreadArray(__spreadArray([], circleTiles.slice(gapStartIndex), true), circleTiles.slice(0, gapStartIndex), true);
                        }
                        var totalSweep_1 = 0;
                        circleTiles.forEach(function (t) {
                            var _a, _b, _c, _d;
                            var td = t.data;
                            totalSweep_1 +=
                                (_d = (_a = td.customSweep) !== null && _a !== void 0 ? _a : (_c = (_b = getFractionDimensions(td.fractionSize)) === null || _b === void 0 ? void 0 : _b.circle) === null || _c === void 0 ? void 0 : _c.sweep) !== null && _d !== void 0 ? _d : 0;
                        });
                        if (totalSweep_1 > 360)
                            totalSweep_1 = 360;
                        var fractionStrings = circleTiles.map(function (t) {
                            var td = t.data;
                            if (td.gluedFrom && td.gluedFrom.length > 0) {
                                return sumAndReduceFractions(td.gluedFrom.map(function (f) { return f.fractionSize; }));
                            }
                            return td.fractionSize;
                        });
                        var newFractionSize = sumAndReduceFractions(fractionStrings);
                        var parsedFraction = parseFraction(newFractionSize) || {
                            numerator: 1,
                            denominator: 1,
                        };
                        var combinedNumerator = parsedFraction.numerator;
                        var combinedDenominator = parsedFraction.denominator === 0 ? 1 : parsedFraction.denominator;
                        var firstTile_1 = circleTiles[0].data;
                        var anchorAbsoluteStart = getAbsoluteStart_1(circleTiles[0]);
                        var newGluedId = "fractionTiles_".concat(uuidv4().replace(/-/g, ''));
                        // Re-arrange for metadata. Flatten each selected tile into its true leaf
                        // pieces first — a selected tile may itself already be a compound from a
                        // prior glue, and gluedFrom must stay a flat array of leaves (see
                        // flattenToLeaves above).
                        var currentAbsoluteStart_1 = anchorAbsoluteStart;
                        var rearrangedGluedFrom = circleTiles.flatMap(function (t) {
                            return flattenToLeaves_1(t).map(function (leaf) {
                                var _a, _b;
                                var config = ((_a = getFractionDimensions(leaf.fractionSize)) === null || _a === void 0 ? void 0 : _a.circle) || {
                                    start: 0,
                                    sweep: 360,
                                };
                                var sweep = (_b = leaf.customSweep) !== null && _b !== void 0 ? _b : config.sweep;
                                var metaTile = __assign(__assign({}, leaf), { center: __assign({}, firstTile_1.center), 
                                    // Calculate rotation such that (rotation + config.start) == currentAbsoluteStart.
                                    // Rounded to whole degrees — see getAbsoluteStart's rounding note above; without
                                    // this the stepped sum below compounds float error across each wedge in the run.
                                    rotation: Math.round((currentAbsoluteStart_1 - (leaf.customSweep != null ? 0 : config.start) + 360) %
                                        360) });
                                currentAbsoluteStart_1 = Math.round((currentAbsoluteStart_1 + sweep) % 360);
                                return metaTile;
                            });
                        });
                        var gluedTile = {
                            type: 'fractionTiles',
                            data: __assign(__assign({}, JSON.parse(JSON.stringify(firstTile_1))), { id: newGluedId, type: 'fractionTiles', center: __assign({}, firstTile_1.center), dimensions: { width: 300, height: 300 }, defaultDimensions: { width: 300, height: 300 }, scale: (_k = firstTile_1.scale) !== null && _k !== void 0 ? _k : 1, lock: { positioning: false, editing: false }, 
                                // Combining always yields a standalone tile — never inherit the
                                // source tile's group membership.
                                groupId: undefined, groupPosition: undefined, groupCenter: undefined, beforeGroupScale: undefined, groupScale: undefined, fractionSize: newFractionSize, shapeType: 'circle', lablesType: firstTile_1.lablesType, customLabel: '', color: firstTile_1.color, customSweep: parsedFraction.denominator === 0 ? 360 * parsedFraction.numerator : totalSweep_1, customNumerator: combinedNumerator, customDenominator: combinedDenominator, rotation: parsedFraction.denominator === 0 ? 0 : anchorAbsoluteStart, gluedFrom: rearrangedGluedFrom }),
                            widgetId: "fractionTiles:".concat(uuidv4().replace(/-/g, '')),
                        };
                        stateChanges.push({
                            type: 'glueFractionTiles',
                            id: newGluedId.split('_')[1],
                            propertyName: 'glue',
                            old: JSON.stringify({
                                removedTiles: circleTiles.map(function (t) { return JSON.parse(JSON.stringify(t)); }),
                                createdTile: gluedTile,
                            }),
                        });
                        newGluedTiles_1.push(gluedTile);
                        removedTileIds_1.push.apply(removedTileIds_1, circleTiles.map(function (t) { return t.data.id; }));
                    }
                    // 2. Glue all selected rectangle tiles into one (if 2+)
                    if (rectTiles.length >= 2) {
                        // Determine orientation: vertical column vs horizontal row
                        var minX = Math.min.apply(Math, rectTiles.map(function (t) { return t.data.center.x; }));
                        var maxX = Math.max.apply(Math, rectTiles.map(function (t) {
                            return t.data.center.x +
                                t.data.dimensions.width;
                        }));
                        var minY = Math.min.apply(Math, rectTiles.map(function (t) { return t.data.center.y; }));
                        var maxY = Math.max.apply(Math, rectTiles.map(function (t) {
                            return t.data.center.y +
                                t.data.dimensions.height;
                        }));
                        var xRange = maxX - minX;
                        var yRange = maxY - minY;
                        // Heuristic for vertical column: spread in X is small relative to spread in Y
                        // and spread in X is less than a typical tile width (e.g., 55 or even less if snapped)
                        var isVerticalGlue_1 = xRange < yRange && xRange < 20;
                        if (isVerticalGlue_1) {
                            // Sort by Y position for top-to-bottom arrangement
                            rectTiles.sort(function (a, b) {
                                return a.data.center.y - b.data.center.y;
                            });
                        }
                        else {
                            // Sort by X position for natural left-to-right arrangement
                            rectTiles.sort(function (a, b) {
                                return a.data.center.x - b.data.center.x;
                            });
                        }
                        var totalWidth_1 = isVerticalGlue_1
                            ? rectTiles[0].data.dimensions.width
                            : 0;
                        var totalHeight_1 = isVerticalGlue_1
                            ? 0
                            : rectTiles[0].data.dimensions.height;
                        rectTiles.forEach(function (t) {
                            var td = t.data;
                            if (isVerticalGlue_1) {
                                totalHeight_1 += td.dimensions.height;
                            }
                            else {
                                totalWidth_1 += td.dimensions.width;
                            }
                        });
                        var fractionStrings = rectTiles.map(function (t) {
                            var td = t.data;
                            if (td.gluedFrom && td.gluedFrom.length > 0) {
                                return sumAndReduceFractions(td.gluedFrom.map(function (f) { return f.fractionSize; }));
                            }
                            return td.fractionSize;
                        });
                        var newFractionSize = sumAndReduceFractions(fractionStrings);
                        var parsedFraction = parseFraction(newFractionSize) || {
                            numerator: 1,
                            denominator: 1,
                        };
                        var finalNumerator = parsedFraction.numerator;
                        var finalDenominator = parsedFraction.denominator === 0 ? 1 : parsedFraction.denominator;
                        var firstRect = rectTiles[0].data;
                        var newGluedId = "fractionTiles_".concat(uuidv4().replace(/-/g, ''));
                        // Re-arrange for metadata. Flatten each selected tile into its true leaf
                        // pieces first — see flattenToLeaves above.
                        var currentX_1 = firstRect.center.x;
                        var currentY_1 = firstRect.center.y;
                        var rearrangedGluedFrom = rectTiles.flatMap(function (t) {
                            return flattenToLeaves_1(t).map(function (leaf) {
                                var metaTile = __assign(__assign({}, leaf), { center: { x: currentX_1, y: currentY_1 } });
                                if (isVerticalGlue_1) {
                                    currentY_1 += leaf.dimensions.height;
                                }
                                else {
                                    currentX_1 += leaf.dimensions.width;
                                }
                                return metaTile;
                            });
                        });
                        var gluedTile = {
                            type: 'fractionTiles',
                            data: __assign(__assign({}, JSON.parse(JSON.stringify(firstRect))), { id: newGluedId, 
                                // Combining always yields a standalone tile — never inherit the
                                // source tile's group membership.
                                groupId: undefined, groupPosition: undefined, groupCenter: undefined, beforeGroupScale: undefined, groupScale: undefined, 
                                // Nor the source tile's number-line attachment — the glued
                                // tile's size is the combined width/height below, not derived
                                // from `firstRect.scale`, so a carried-over `numberLineId`
                                // would leave it pointing at a number line whose scale no
                                // longer matches its actual size.
                                numberLineId: undefined, preSnapScale: undefined, fractionSize: newFractionSize, dimensions: { width: totalWidth_1, height: totalHeight_1 }, defaultDimensions: {
                                    width: totalWidth_1 / ((_l = firstRect.scale) !== null && _l !== void 0 ? _l : 1),
                                    height: totalHeight_1 / ((_m = firstRect.scale) !== null && _m !== void 0 ? _m : 1),
                                }, customLabel: '', customNumerator: finalNumerator, customDenominator: finalDenominator, gluedFrom: rearrangedGluedFrom }),
                            widgetId: "fractionTiles:".concat(uuidv4().replace(/-/g, '')),
                        };
                        stateChanges.push({
                            type: 'glueFractionTiles',
                            id: newGluedId.split('_')[1],
                            propertyName: 'glue',
                            old: JSON.stringify({
                                removedTiles: rectTiles.map(function (t) { return JSON.parse(JSON.stringify(t)); }),
                                createdTile: gluedTile,
                            }),
                        });
                        newGluedTiles_1.push(gluedTile);
                        removedTileIds_1.push.apply(removedTileIds_1, rectTiles.map(function (t) { return t.data.id; }));
                    }
                    if (newGluedTiles_1.length > 0) {
                        // Any surviving tile whose `snappedTo.parentId` pointed at one of the
                        // consumed source tiles is now dangling -- it re-links to whichever
                        // new glued tile it's still edge-touching (rather than simply
                        // clearing the relation) so combining tiles doesn't silently unsnap
                        // everything that was attached to the sources.
                        var newGluedRectPieces_1 = newGluedTiles_1
                            .map(function (t) { return t.data; })
                            .filter(function (t) { return t.shapeType === 'rectangle'; });
                        var orphanedSnapChanges_1 = [];
                        var clearedManipulative_1 = manipulative.map(function (m) {
                            if (m.data.type !== 'fractionTiles')
                                return m;
                            var d = m.data;
                            if (removedTileIds_1.includes(d.id) || !d.snappedTo)
                                return m;
                            if (!removedTileIds_1.includes(d.snappedTo.parentId))
                                return m;
                            var relinked = (newGluedRectPieces_1.length &&
                                findTouchingParent(d.center, d.dimensions, newGluedRectPieces_1)) ||
                                undefined;
                            orphanedSnapChanges_1.push({
                                type: d.id,
                                id: d.id.split('_')[1],
                                propertyName: 'snappedTo',
                                old: JSON.stringify(d.snappedTo),
                                new: JSON.stringify(relinked !== null && relinked !== void 0 ? relinked : null),
                            });
                            return __assign(__assign({}, m), { data: __assign(__assign({}, d), { snappedTo: relinked }) });
                        });
                        saveState(__spreadArray(__spreadArray([], stateChanges, true), orphanedSnapChanges_1, true));
                        setManipulative(function () { return __spreadArray(__spreadArray([], clearedManipulative_1.filter(function (m) { return !m.data.id || !removedTileIds_1.includes(m.data.id); }), true), newGluedTiles_1, true); });
                        var newIds = newGluedTiles_1.map(function (t) { return t.data.id; });
                        setSelectedTool(newIds);
                        setSelectedManipulativeId(newIds[0]);
                        playAudio('fractionTileTape');
                        // Delay so focus transitions settle before the assertive live region fires;
                        // otherwise VoiceOver / JAWS cancels the announcement with the focus speech.
                        setTimeout(function () { return announceAssertive('Tiles combined'); }, 200);
                    }
                    else {
                        // Attempt Unglue logic remains same if applicable
                        var gluedTiles = selectedTiles.filter(function (t) {
                            var td = t.data;
                            return td.gluedFrom && td.gluedFrom.length > 0;
                        });
                        if (gluedTiles.length > 0 && selectedTiles.length === gluedTiles.length) {
                            var allRestoredTiles_1 = [];
                            var removedIds_1 = [];
                            gluedTiles.forEach(function (gluedTile) {
                                var gluedTileData = gluedTile.data;
                                // Flatten defensively — self-heals any pre-existing nested gluedFrom
                                // data (from before glue was fixed to always flatten) so a single
                                // unglue always fully restores true leaves.
                                var originalTilesData = flattenToLeaves_1(gluedTile);
                                var originalGroupCenter = { x: 0, y: 0 };
                                if (gluedTileData.shapeType === 'rectangle') {
                                    var minX = Math.min.apply(Math, originalTilesData.map(function (t) { return t.center.x; }));
                                    var avgY = originalTilesData.reduce(function (sum, t) { return sum + t.center.y; }, 0) /
                                        originalTilesData.length;
                                    originalGroupCenter = { x: minX, y: avgY };
                                }
                                else {
                                    originalGroupCenter = originalTilesData[0].center;
                                }
                                var deltaX = gluedTileData.center.x - originalGroupCenter.x;
                                var deltaY = gluedTileData.center.y - originalGroupCenter.y;
                                var restoredTiles = originalTilesData.map(function (oldData) {
                                    var newId = "fractionTiles_".concat(uuidv4().replace(/-/g, ''));
                                    var newCenter = {
                                        x: oldData.center.x + deltaX,
                                        y: oldData.center.y + deltaY,
                                    };
                                    return {
                                        type: 'fractionTiles',
                                        data: __assign(__assign({}, oldData), { id: newId, center: newCenter, 
                                            // Restored tiles' pre-glue relationships referenced ids that no
                                            // longer exist post-glue -- start unsnapped.
                                            snappedTo: undefined, lock: { positioning: false, editing: false } }),
                                        widgetId: "fractionTiles:".concat(uuidv4().replace(/-/g, '')),
                                    };
                                });
                                var oldTileData = JSON.parse(JSON.stringify(gluedTile));
                                newStateToSave.push({
                                    type: 'glueFractionTiles',
                                    id: gluedTileData.id.split('_')[1],
                                    propertyName: 'unglue',
                                    old: JSON.stringify({ removedTile: oldTileData, createdTiles: restoredTiles }),
                                });
                                allRestoredTiles_1.push.apply(allRestoredTiles_1, restoredTiles);
                                removedIds_1.push(gluedTileData.id);
                            });
                            setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev.filter(function (m) { return !removedIds_1.includes(m.data.id); }), true), allRestoredTiles_1, true); });
                            var newIds = allRestoredTiles_1.map(function (t) { return t.data.id; });
                            setSelectedTool(newIds);
                            setSelectedManipulativeId(newIds[0]);
                            setTimeout(function () { return announceAssertive('Tiles separated'); }, 200);
                        }
                    }
                    break;
                }
                case 'splitFractionTile':
                    if ((_o = data.data) === null || _o === void 0 ? void 0 : _o.currentDenominator) {
                        setSplitFractionTileCurrentDenominator(data.data.currentDenominator);
                    }
                    if ((_p = data.data) === null || _p === void 0 ? void 0 : _p.currentNumerator) {
                        setSplitFractionTileCurrentNumerator(data.data.currentNumerator);
                    }
                    setShowSplitFractionTileModal(true);
                    break;
                case 'splitFractionTileDone': {
                    var parts_1 = data.data.parts;
                    if (!parts_1 || parts_1 < 2)
                        break;
                    var selectedId_1 = selectedTool[0];
                    if (!selectedId_1 || !selectedId_1.startsWith('fractionTiles_'))
                        break;
                    // selectedTool may carry a suffix from label focus (e.g. _custom_label_rect_1).
                    // Match via startsWith to find the actual tile.
                    var parentTile = manipulative.find(function (m) {
                        return m.data.type === 'fractionTiles' &&
                            (m.data.id === selectedId_1 || selectedId_1.startsWith(m.data.id + '_'));
                    });
                    if (!parentTile)
                        break;
                    var parentData = parentTile.data;
                    // Calculate current value as a fraction
                    var currentNumerator = (_q = parentData.customNumerator) !== null && _q !== void 0 ? _q : 1;
                    var currentDenominator = (_r = parentData.customDenominator) !== null && _r !== void 0 ? _r : getDenominatorFromFractionType(parentData.fractionSize);
                    // New fraction for each piece: (currentNumerator / currentDenominator) / parts
                    var finalNumerator = currentNumerator;
                    var finalDenominator = currentDenominator * parts_1;
                    // Simplify the fraction
                    var common = gcd(finalNumerator, finalDenominator);
                    finalNumerator /= common;
                    finalDenominator /= common;
                    var newFractionType = finalDenominator === 1
                        ? "fraction-".concat(finalNumerator)
                        : "fraction-".concat(finalNumerator, "-").concat(finalDenominator);
                    var isCircle = parentData.shapeType === 'circle';
                    var newTiles_1 = [];
                    if (isCircle) {
                        var actualSweep = (_v = (_s = parentData.customSweep) !== null && _s !== void 0 ? _s : (_u = (_t = FRACTION_CONFIG[parentData.fractionSize]) === null || _t === void 0 ? void 0 : _t.circle) === null || _u === void 0 ? void 0 : _u.sweep) !== null && _v !== void 0 ? _v : 360;
                        var newSweep = actualSweep / parts_1;
                        // Get the absolute start rotation of the parent
                        var startRotation = (_w = parentData.rotation) !== null && _w !== void 0 ? _w : 0;
                        if (parentData.customSweep == null) {
                            var config = ((_x = FRACTION_CONFIG[parentData.fractionSize]) === null || _x === void 0 ? void 0 : _x.circle) || {
                                start: 0,
                                sweep: 360,
                            };
                            startRotation = (config.start + startRotation) % 360;
                        }
                        var circleSplitSnappedId = uuidv4();
                        for (var i = 0; i < parts_1; i++) {
                            var newId = "fractionTiles_".concat(uuidv4().replace(/-/g, ''));
                            var newTile = {
                                type: 'fractionTiles',
                                data: __assign(__assign({}, JSON.parse(JSON.stringify(parentData))), { id: newId, fractionSize: newFractionType, customSweep: newSweep, rotation: (startRotation + i * newSweep) % 360, customNumerator: finalNumerator, customDenominator: finalDenominator, gluedFrom: undefined, customLabel: undefined, snappedId: circleSplitSnappedId, snappedTo: undefined }),
                                widgetId: "fractionTiles:".concat(uuidv4().replace(/-/g, '')),
                            };
                            newTiles_1.push(newTile);
                        }
                    }
                    else {
                        var parentWidth = parentData.dimensions.width;
                        var parentHeight = parentData.dimensions.height;
                        var parentLeft = (_1 = (_z = (_y = parentData.center) === null || _y === void 0 ? void 0 : _y.x) !== null && _z !== void 0 ? _z : (_0 = parentData.position) === null || _0 === void 0 ? void 0 : _0.x) !== null && _1 !== void 0 ? _1 : 0;
                        var parentCenterY = (_5 = (_3 = (_2 = parentData.center) === null || _2 === void 0 ? void 0 : _2.y) !== null && _3 !== void 0 ? _3 : (_4 = parentData.position) === null || _4 === void 0 ? void 0 : _4.y) !== null && _5 !== void 0 ? _5 : 0;
                        var parentScale = (_6 = parentData.scale) !== null && _6 !== void 0 ? _6 : 1;
                        // Use exact fractional width so every tile is identical and all parts together
                        // span exactly the parent width. The browser handles subpixel rendering uniformly.
                        var newWidth = parentWidth / parts_1;
                        var rectSplitSnappedId = uuidv4();
                        var prevId = void 0;
                        for (var i = 0; i < parts_1; i++) {
                            var newId = "fractionTiles_".concat(uuidv4().replace(/-/g, ''));
                            var newLeft = parentLeft + i * newWidth;
                            var newTile = {
                                type: 'fractionTiles',
                                data: __assign(__assign({}, JSON.parse(JSON.stringify(parentData))), { id: newId, fractionSize: newFractionType, dimensions: { width: newWidth, height: parentHeight }, defaultDimensions: {
                                        width: newWidth / parentScale,
                                        height: parentHeight / parentScale,
                                    }, center: { x: newLeft, y: parentCenterY }, position: { x: newLeft, y: parentCenterY, z: (_7 = parentData.position) === null || _7 === void 0 ? void 0 : _7.z }, customNumerator: finalNumerator, customDenominator: finalDenominator, gluedFrom: undefined, customLabel: undefined, snappedId: rectSplitSnappedId, 
                                    // Each split piece stays flush against its immediate predecessor so
                                    // the whole split row still scales as a chain -- flush chaining
                                    // (segmentIndex 0 of a single-segment "parent").
                                    snappedTo: prevId
                                        ? { parentId: prevId, edge: 'right', segmentIndex: 0, segmentCount: 1 }
                                        : undefined }),
                                widgetId: "fractionTiles:".concat(uuidv4().replace(/-/g, '')),
                            };
                            newTiles_1.push(newTile);
                            prevId = newId;
                        }
                    }
                    var parentId_1 = parentTile.data.id;
                    newStateToSave.push({
                        type: 'splitFractionTile',
                        id: parentId_1.split('_')[1],
                        propertyName: 'split',
                        old: JSON.stringify({ removedTile: parentTile, createdTiles: newTiles_1 }),
                    });
                    // Any other tile whose `snappedTo.parentId` was the pre-split tile's id
                    // is now dangling -- that relationship no longer resolves to anything.
                    // It re-links to whichever new split piece it's still edge-touching
                    // (e.g. a row above/below the split tile spans multiple new pieces)
                    // rather than simply dropping the relation, so split doesn't silently
                    // unsnap every tile that was attached to the pre-split tile.
                    var newRectPieces_1 = newTiles_1
                        .map(function (t) { return t.data; })
                        .filter(function (t) { return t.shapeType === 'rectangle'; });
                    var orphanedBySplit = manipulative.filter(function (m) {
                        var _a;
                        if (m.data.type !== 'fractionTiles')
                            return false;
                        var d = m.data;
                        return ((_a = d.snappedTo) === null || _a === void 0 ? void 0 : _a.parentId) === parentId_1;
                    });
                    orphanedBySplit.forEach(function (m) {
                        var d = m.data;
                        var relinked = (newRectPieces_1.length &&
                            findTouchingParent(d.center, d.dimensions, newRectPieces_1)) ||
                            undefined;
                        newStateToSave.push({
                            type: d.id,
                            id: d.id.split('_')[1],
                            propertyName: 'snappedTo',
                            old: JSON.stringify(d.snappedTo),
                            new: JSON.stringify(relinked !== null && relinked !== void 0 ? relinked : null),
                        });
                    });
                    setManipulative(function (prev) {
                        return prev
                            .filter(function (m) { return m.data.id !== parentId_1; })
                            .map(function (m) {
                            var _a;
                            if (m.data.type !== 'fractionTiles')
                                return m;
                            var d = m.data;
                            if (((_a = d.snappedTo) === null || _a === void 0 ? void 0 : _a.parentId) !== parentId_1)
                                return m;
                            var relinked = (newRectPieces_1.length &&
                                findTouchingParent(d.center, d.dimensions, newRectPieces_1)) ||
                                undefined;
                            return __assign(__assign({}, m), { data: __assign(__assign({}, d), { snappedTo: relinked }) });
                        })
                            .concat(newTiles_1);
                    });
                    var newIds = newTiles_1.map(function (t) { return t.data.id; }).filter(function (id) { return !!id; });
                    setSelectedManipulativeId(newIds[0]);
                    // Select only the first split tile so the auto-focus guard fires and
                    // keyboard focus lands on it (selectedTool.length must equal 1).
                    setSelectedTool([newIds[0]]);
                    playAudio('scissor');
                    setTimeout(function () { return announceAssertive("Tile split into ".concat(parts_1, " parts")); }, 200);
                    break;
                }
                case 'scaleMinus':
                case 'scalePlus': {
                    // When the label MathEditor is focused, selectedTool contains
                    // "fractionTiles_xxx_custom_label_rect_1" — strip the label suffix
                    // to recover the actual tile ID.
                    var toTileId_1 = function (t) { return t.replace(/_custom_label_(rect|circle)_\d+$/, ''); };
                    // Resolve selectedTool to concrete fraction-tile ids: a manipulativeGroup_
                    // entry (the group is the active selection) expands to its member tile
                    // ids, exactly like NumberAndMoneyPieceManipulative resolves group
                    // selections before acting on them — otherwise scale +/- would silently
                    // no-op whenever a fraction-tile group (rather than a bare tile id) is
                    // the current selection.
                    var resolvedFtIds = selectedTool.flatMap(function (t) {
                        if (t.startsWith('manipulativeGroup_')) {
                            return manipulative
                                .filter(function (m) { return m.data.type === 'fractionTiles' && m.data.groupId === t; })
                                .map(function (m) { return m.data.id; });
                        }
                        return t.startsWith('fractionTiles_') ? [toTileId_1(t)] : [];
                    });
                    // Only handle when at least one fraction tile is selected (directly or via a group)
                    var hasFractionTileSelected = resolvedFtIds.length > 0;
                    if (!hasFractionTileSelected)
                        break;
                    // Collect the shape types (rectangle / circle) of the selected fraction tiles.
                    var selectedShapeTypes_1 = new Set(resolvedFtIds
                        .map(function (tileId) {
                        var tile = manipulative.find(function (m) { return m.data.id === tileId; });
                        return tile ? tile.data.shapeType : null;
                    })
                        .filter(function (s) { return s !== null; }));
                    // Scale every fraction tile matching the selected shape type(s), not just
                    // the ones currently selected — tiles of the same shape scale in lockstep.
                    var tilesToScale_1 = manipulative.filter(function (m) {
                        return m.data.type === 'fractionTiles' &&
                            selectedShapeTypes_1.has(m.data.shapeType);
                    });
                    if (tilesToScale_1.length === 0)
                        break;
                    // The shared/global scale for each selected shape type, and where it
                    // moves to. Number-line-attached tiles keep their NL-forced `scale`,
                    // so the global scale is read from `getGlobalFractionTileScale`
                    // (an unattached sibling, else an attached tile's `preSnapScale`)
                    // rather than from any one tile's own `scale`.
                    var newGlobalScaleByShape_1 = new Map();
                    selectedShapeTypes_1.forEach(function (shape) {
                        var oldGlobal = getGlobalFractionTileScale(manipulative, shape);
                        newGlobalScaleByShape_1.set(shape, data.id === 'scaleMinus' ? Math.max(0.5, oldGlobal - 0.1) : oldGlobal + 0.1);
                    });
                    // Announce the new scale to screen readers using the first tile's shape.
                    // All selected tiles scale together, so one announcement is sufficient.
                    {
                        var firstTileData = tilesToScale_1[0].data;
                        var newScale_1 = Math.round(((_8 = newGlobalScaleByShape_1.get(firstTileData.shapeType)) !== null && _8 !== void 0 ? _8 : 1) * 10) / 10;
                        setTimeout(function () { return announceAssertive("Scale updated to ".concat(newScale_1.toFixed(1))); }, 200);
                    }
                    // Pre-compute new scale, dimensions, and base info for each tile
                    var tileScaleInfo_1 = new Map();
                    // Tiles attached to a number line are NOT resized: their size is
                    // dictated by the number line's interval, not by the global scale.
                    // They only track the new global scale in `preSnapScale`, which is
                    // what they restore to when they are later dragged off the line.
                    var nlTrackedScale_1 = new Map();
                    tilesToScale_1.forEach(function (m) {
                        var _a, _b;
                        var tileData = m.data;
                        if (tileData.numberLineId) {
                            var newGlobal = newGlobalScaleByShape_1.get(tileData.shapeType);
                            if (newGlobal === undefined || newGlobal === tileData.preSnapScale)
                                return;
                            nlTrackedScale_1.set(tileData.id, newGlobal);
                            newStateToSave.push({
                                type: tileData.id,
                                id: tileData.id.split('_')[1],
                                propertyName: 'preSnapScale',
                                old: JSON.stringify(tileData.preSnapScale),
                                new: JSON.stringify(newGlobal),
                            });
                            return;
                        }
                        var oldScale = (_a = tileData.scale) !== null && _a !== void 0 ? _a : 1;
                        var newScale = data.id === 'scaleMinus' ? Math.max(0.5, oldScale - 0.1) : oldScale + 0.1;
                        var isGlued = tileData.gluedFrom && tileData.gluedFrom.length > 0;
                        var baseDim = (_b = tileData.defaultDimensions) !== null && _b !== void 0 ? _b : (function () {
                            var _a, _b;
                            var config = getFractionDimensions(tileData.fractionSize);
                            return { width: (_b = (_a = config === null || config === void 0 ? void 0 : config.rectangle) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 660, height: 55 };
                        })();
                        var baseWidth = tileData.shapeType === 'circle' && !isGlued ? 300 : baseDim.width;
                        var baseHeight = tileData.shapeType === 'circle' && !isGlued ? 300 : baseDim.height;
                        tileScaleInfo_1.set(tileData.id, {
                            newScale: newScale,
                            newWidth: baseWidth * newScale,
                            newHeight: baseHeight * newScale,
                        });
                        newStateToSave.push({
                            type: tileData.id,
                            id: tileData.id.split('_')[1],
                            propertyName: 'scale',
                            old: JSON.stringify(oldScale),
                            new: JSON.stringify(newScale),
                        });
                    });
                    // For rectangles: reposition tiles attached via a stored `snappedTo`
                    // relationship so they stay flush against their parent segment after
                    // scaling. This is a parent-first recursive walk over the explicit
                    // relationship data recorded at snap time — not a live geometry
                    // re-derivation (see docs/manipulatives/FractionTile.md).
                    var newPositions_1 = {};
                    var rectTiles = tilesToScale_1
                        .map(function (m) { return m.data; })
                        .filter(function (d) { return d.shapeType === 'rectangle'; });
                    var rectById_1 = new Map(rectTiles.map(function (t) { return [t.id, t]; }));
                    var resolved_1 = new Set();
                    var visiting_1 = new Set(); // cycle guard
                    var resolvePosition_1 = function (tile) {
                        var _a, _b, _c, _d, _e, _f;
                        if (resolved_1.has(tile.id))
                            return (_a = newPositions_1[tile.id]) !== null && _a !== void 0 ? _a : tile.center;
                        // Number-line-attached tiles are not rescaled, so they must not be
                        // repositioned either — their position belongs to the number line.
                        if (((_b = tile.lock) === null || _b === void 0 ? void 0 : _b.positioning) || tile.numberLineId) {
                            resolved_1.add(tile.id);
                            return tile.center;
                        }
                        if (visiting_1.has(tile.id)) {
                            // Cycle detected — treat as unresolved rather than recursing forever.
                            resolved_1.add(tile.id);
                            return tile.center;
                        }
                        visiting_1.add(tile.id);
                        var rel = tile.snappedTo;
                        var parent = rel && rectById_1.get(rel.parentId);
                        var pos;
                        if (!rel || !parent) {
                            // Unsnapped, or the parent is no longer present (deleted/glued away) — stays put.
                            pos = tile.center;
                        }
                        else {
                            var parentPos = resolvePosition_1(parent);
                            var parentInfo = tileScaleInfo_1.get(parent.id);
                            var parentNewW = (_c = parentInfo === null || parentInfo === void 0 ? void 0 : parentInfo.newWidth) !== null && _c !== void 0 ? _c : parent.dimensions.width;
                            var parentNewH = (_d = parentInfo === null || parentInfo === void 0 ? void 0 : parentInfo.newHeight) !== null && _d !== void 0 ? _d : parent.dimensions.height;
                            var myInfo = tileScaleInfo_1.get(tile.id);
                            var myNewW = (_e = myInfo === null || myInfo === void 0 ? void 0 : myInfo.newWidth) !== null && _e !== void 0 ? _e : tile.dimensions.width;
                            var myNewH = (_f = myInfo === null || myInfo === void 0 ? void 0 : myInfo.newHeight) !== null && _f !== void 0 ? _f : tile.dimensions.height;
                            var segW = parentNewW / (rel.segmentCount || 1);
                            // Right-aligned docks keep their right edge flush with the
                            // parent's as widths change; left-anchoring them here would
                            // slide the child off the parent's corner (see `alignRight`).
                            var stackedX = rel.alignRight
                                ? parentPos.x + parentNewW - myNewW
                                : parentPos.x + rel.segmentIndex * segW;
                            if (rel.edge === 'left')
                                pos = { x: parentPos.x - myNewW, y: parentPos.y };
                            else if (rel.edge === 'right')
                                pos = { x: parentPos.x + parentNewW, y: parentPos.y };
                            else if (rel.edge === 'bottom')
                                pos = { x: stackedX, y: parentPos.y + parentNewH };
                            else
                                pos = { x: stackedX, y: parentPos.y - myNewH }; // top
                        }
                        newPositions_1[tile.id] = pos;
                        resolved_1.add(tile.id);
                        visiting_1.delete(tile.id);
                        return pos;
                    };
                    rectTiles.forEach(resolvePosition_1);
                    // Drop no-op entries (tile resolved to its own original position).
                    Object.keys(newPositions_1).forEach(function (tileId) {
                        var tile = rectById_1.get(tileId);
                        if (!tile)
                            return;
                        var pos = newPositions_1[tileId];
                        if (Math.abs(pos.x - tile.center.x) < 0.1 && Math.abs(pos.y - tile.center.y) < 0.1) {
                            delete newPositions_1[tileId];
                        }
                    });
                    // Save undo entries for any position changes
                    Object.entries(newPositions_1).forEach(function (_a) {
                        var tileId = _a[0], newCenter = _a[1];
                        var original = tilesToScale_1.find(function (m) { return m.data.id === tileId; });
                        if (original) {
                            newStateToSave.push({
                                type: tileId,
                                id: tileId.split('_')[1],
                                propertyName: 'center',
                                old: JSON.stringify(original.data.center),
                                new: JSON.stringify(newCenter),
                            });
                        }
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            if (item.data.type !== 'fractionTiles')
                                return item;
                            var tileData = item.data;
                            if (!selectedShapeTypes_1.has(tileData.shapeType))
                                return item;
                            // NL-attached: track the new global scale for a later unsnap,
                            // but leave scale/dimensions/center alone.
                            var trackedScale = nlTrackedScale_1.get(tileData.id);
                            if (trackedScale !== undefined) {
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { preSnapScale: trackedScale }) });
                            }
                            var info = tileScaleInfo_1.get(tileData.id);
                            if (!info)
                                return item;
                            var newCenter = newPositions_1[tileData.id]
                                ? newPositions_1[tileData.id]
                                : tileData.center;
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: info.newScale, dimensions: { width: info.newWidth, height: info.newHeight }, center: newCenter }) });
                        });
                    });
                    break;
                }
            }
            // Save all state changes as a batch if any
            if (newStateToSave.length > 0) {
                saveState(newStateToSave);
                customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
            }
        };
        customEventHandler.addEventListener('bottomPanel', handleEvents);
        customEventHandler.addEventListener('sidebarClicked', handleEvents);
        customEventHandler.addEventListener('headerClicked', handleEvents);
        customEventHandler.addEventListener('bottomBar', handleEvents);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', handleEvents);
            customEventHandler.removeEventListener('sidebarClicked', handleEvents);
            customEventHandler.removeEventListener('headerClicked', handleEvents);
            customEventHandler.removeEventListener('bottomBar', handleEvents);
        };
    }, [
        manipulative,
        selectedTool,
        selectedManipulativeId,
        zoomLevel,
        playerBgPattern,
        setShowSplitFractionTileModal,
        setSplitFractionTileCurrentDenominator,
        setManipulative,
        setSelectedTool,
        setSelectedManipulativeId,
    ]);
    return null;
};
export default FractionTileController;
